import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import style from './style.module.less';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { actions } from '../../redux/slices';
import selectors from '../../redux/selectors';
import { MessagesComponent } from './messages';
import { ChatInput } from './input';
import { LawyerConnection, Message, MessageType } from '@law-connect/types';
import { ChatHeader } from './header';
import { ActionModal } from '../../components/action-modal';
import { usePrevious } from '../../utils/use-previous';
import ScrollToBottom, {
  useScrollToBottom,
  useObserveScrollPosition,
} from 'react-scroll-to-bottom';

export const ChatContainer: FC = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const [isOpenRequestModal, setIsOpenRequestModal] = useState(false);
  const connection = useAppSelector(
    selectors.lawyerConnection.getConnectionById(id)
  );
  const messages = useAppSelector(
    selectors.lawyerConnection.getMessagesById(id)
  );
  const files = useAppSelector(selectors.file.getFiles(id));
  const isFetchingFiles = useAppSelector(selectors.file.isFetchingFiles());
  const prevIsFetchingFiles = usePrevious(isFetchingFiles);
  const fetchFilesError = useAppSelector(selectors.file.getFetchFilesError());

  // we want to fetch the files for the message if we cant find the client file
  useEffect(() => {
    if (
      !files &&
      !isFetchingFiles &&
      !prevIsFetchingFiles &&
      !fetchFilesError
    ) {
      dispatch(actions.file.fetch({ connectionId: id }));
    }
  }, [
    id,
    dispatch,
    fetchFilesError,
    files,
    isFetchingFiles,
    prevIsFetchingFiles,
  ]);

  const onSendMessage = (args: { text: string; files: File[] }) => {
    if (args.text?.length) {
      dispatch(
        actions.lawyerConnection.sendMessage({
          id,
          message: args.text,
        })
      );
    }
    // dispatch files here yay
    if (args.files?.length) {
      dispatch(actions.file.sendFiles({ id, files: args.files }));
    }
  };

  useEffect(() => {
    dispatch(actions.lawyerConnection.fetchMessages({ id }));
    dispatch(
      actions.lawyerConnection.pollMessages({ connectionId: id, init: true })
    );
    return () => {
      dispatch(actions.lawyerConnection.stopPollMessages({ connectionId: id }));
    };
  }, [dispatch, id]);

  const closeRequestModal = useCallback(() => setIsOpenRequestModal(false), []);
  const onConfirmRequest = useCallback(() => {
    dispatch(actions.lawyerConnection.requestPermission({ id }));
    closeRequestModal();
  }, [dispatch, id, closeRequestModal]);

  return (
    <>
      <div className={style.container}>
        <ScrollToBottom
          className={style.chatBody}
          followButtonClassName={style.noButton}
        >
          <ScrollBody
            id={id}
            messages={messages ?? []}
            connection={connection}
          />
        </ScrollToBottom>
        <div className={style.chatInputWrapper}>
          <ChatInput
            onSendMessage={onSendMessage}
            disabled={
              connection?.endedAt !== null || connection?.deletedAt !== null
            }
          />
        </div>
      </div>
      {/* request info for permission */}
      <ActionModal
        isOpen={isOpenRequestModal}
        onClose={closeRequestModal}
        // onConfirm={onConfirmRequest}
        title={'Request Info'}
        confirm={{
          text: 'Send',
          onClick: onConfirmRequest,
        }}
      >
        This will request the private information of your potential client
        including case information. Send information request?
      </ActionModal>
    </>
  );
};

interface ScrollBodyProps {
  id: string;
  messages: Message[];
  connection: LawyerConnection;
}

const ScrollBody: FC<ScrollBodyProps> = (props) => {
  const { id, messages, connection } = props;
  const chatRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useScrollToBottom();
  const observer = useCallback(() => {
    // emit window scroll event
    // we are doing this so that the context menu closes when we scroll
    window.dispatchEvent(new CustomEvent('scroll', {}));
  }, []);

  useObserveScrollPosition(observer);

  useEffect(() => {
    scrollToBottom();
  }, [
    scrollToBottom,
    messages?.length,
    connection?.endedAt,
    connection?.deletedAt,
  ]);

  return (
    <div ref={chatRef} className={style.bodyContainer}>
      <ChatHeader chatBodyRef={chatRef} connectionId={id} />
      <MessagesComponent messages={messages ?? []} connectionId={id} />
    </div>
  );
};
